import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  RouterProvider,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { RequireHostAuth } from "@/auth";
import { ErrorScreen, NotFoundScreen } from "@/components/ErrorBoundary";
import { PanelSkeleton } from "@/components/ui/Skeleton";
import { EditorPageSkeleton } from "@/features/editor-shell/EditorSkeleton";
import { GuestInviteSkeleton } from "@/features/guest-invite/GuestInviteSkeleton";
import { ProductShell } from "@/layouts/ProductShell";
import { AuthPage, safeRedirectUrl } from "@/pages/AuthPage";
// Pre-rendered marketing pages stay in the main bundle; everything else loads on demand.
import { DesignsPage } from "@/pages/DesignsPage";
import { HomePage } from "@/pages/HomePage";

const EditorIndexPage = lazyRouteComponent(() => import("@/pages/EditorIndexPage"), "EditorIndexPage");
const EditorProjectPage = lazyRouteComponent(() => import("@/pages/EditorProjectPage"), "EditorProjectPage");
const GuestInvitePage = lazyRouteComponent(() => import("@/pages/GuestInvitePage"), "GuestInvitePage");
const EnvelopeAnimationDemo = lazyRouteComponent(() => import("@/pages/EnvelopeAnimationDemo"), "EnvelopeAnimationDemo");
const StyleGuidePage = lazyRouteComponent(() => import("@/pages/StyleGuidePage"), "StyleGuidePage");

function RouteError({ error, reset }: ErrorComponentProps) {
  return (
    <ErrorScreen
      error={error}
      onRetry={() => {
        reset();
        void router.invalidate();
      }}
    />
  );
}

function PagePending() {
  return (
    <div style={{ maxWidth: "48rem", margin: "4rem auto", padding: "0 1.5rem" }}>
      <PanelSkeleton label="Loading page" />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundScreen,
});

/** Marketing / product routes — shared `ProductShell` chrome. */
const productShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "product-shell",
  component: () => (
    <ProductShell>
      <Outlet />
    </ProductShell>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => productShellRoute,
  path: "/",
  component: HomePage,
});

const styleGuideRoute = createRoute({
  getParentRoute: () => productShellRoute,
  path: "style-guide",
  component: StyleGuidePage,
});

const designsRoute = createRoute({
  getParentRoute: () => productShellRoute,
  path: 'designs',
  validateSearch: (search: Record<string, unknown>): { template?: string } => ({template: typeof search.template === 'string' ? search.template : undefined}),
  component: DesignsPage,
});

/** Editor is full-viewport; hosts must be signed in. */
const editorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "editor",
  component: () => (
    <RequireHostAuth>
      <Outlet />
    </RequireHostAuth>
  ),
});

const editorIndexRoute = createRoute({
  getParentRoute: () => editorRoute,
  path: "/",
  component: EditorIndexPage,
  pendingComponent: PagePending,
});

const editorProjectRoute = createRoute({
  getParentRoute: () => editorRoute,
  path: "$projectId",
  component: EditorProjectPage,
  pendingComponent: EditorPageSkeleton,
});

/** Public guest invite — no product chrome, no auth. */
const guestInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "i/$code",
  validateSearch: (search: Record<string, unknown>) => {
    const raw = search.preview;
    const preview =
      raw === true ||
      raw === "true" ||
      raw === "1" ||
      raw === 1 ||
      raw === '"1"';
    const g = typeof search.g === "string" && search.g ? search.g : undefined;
    return { preview, g } as { preview: boolean; g?: string };
  },
  component: GuestInvitePage,
  pendingComponent: GuestInviteSkeleton,
});

const animationDemoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/envelope-demo',
  validateSearch: (search: Record<string, unknown>): { template?: string } => ({
    template: typeof search.template === 'string' ? search.template : undefined,
  }),
  component: EnvelopeAnimationDemo,
  pendingComponent: GuestInviteSkeleton,
});

const authSearch = (search: Record<string, unknown>): { redirect_url?: string } => ({
  redirect_url: typeof search.redirect_url === "string" ? search.redirect_url : undefined,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in",
  validateSearch: authSearch,
  component: function SignInRoute() {
    const { redirect_url } = signInRoute.useSearch();
    return <AuthPage mode="sign-in" redirectUrl={safeRedirectUrl(redirect_url)} />;
  },
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-up",
  validateSearch: authSearch,
  component: function SignUpRoute() {
    const { redirect_url } = signUpRoute.useSearch();
    return <AuthPage mode="sign-up" redirectUrl={safeRedirectUrl(redirect_url)} />;
  },
});

export const routeTree = rootRoute.addChildren([
  animationDemoRoute,
  signInRoute,
  signUpRoute,
  productShellRoute.addChildren([indexRoute, styleGuideRoute, designsRoute]),
  editorRoute.addChildren([editorIndexRoute, editorProjectRoute]),
  guestInviteRoute,
]);

export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteError,
  defaultPendingComponent: PagePending,
  // Show skeletons only if a lazy chunk takes noticeably long, and then long enough not to flash.
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  // Start downloading a page's code when its link is hovered or focused.
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function ProductApp() {
  return <RouterProvider router={router} />;
}
